import React, { useEffect, useRef, useState } from 'react'

// Antd
import { Button, Col, Drawer, Form, Input, Row, Select, notification, Spin, Radio } from 'antd';

import TextArea from 'antd/es/input/TextArea';

// Quill
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

import { editCourseContent, getCourseById } from '../../../../../../Constants/Api/functions/function';
import { normalizeDriveUrlForSave } from '../../../../../../Utils/driveUtils';

const { Option } = Select;

const modules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ align: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

const EditContent = ({
    isOpen,
    setOpen,
    hadleGetCourceContents,
    contentData
}) => {
    const [api, contextHolder] = notification.useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [course, setCourse] = useState(null);
    const [installmentOptions, setInstallmentOptions] = useState([{ value: 0, label: '0 (Free)' }]);
    const formRef = useRef();

    useEffect(() => {
        if (isOpen && contentData?.courseId) {
            fetchCourseDetails();
        }
    }, [isOpen, contentData?.courseId]);

    const fetchCourseDetails = async () => {
        try {
            let courseData = await getCourseById(contentData.courseId);
            
            // Handle if courseData is an array
            if (Array.isArray(courseData)) {
                courseData = courseData[0];
            }
            
            if (courseData) {
                setCourse(courseData);
                const totalInstallments = courseData.installments || 0;
                const options = [];
                // Generate options from 0 to total installments
                for (let i = 0; i <= totalInstallments; i++) {
                    options.push({ value: i, label: i === 0 ? '(Free)' : `${i}` });
                }
                setInstallmentOptions(options);
            }
        } catch (error) {
            console.error('Error fetching course details:', error);
        }
    };

    useEffect(() => {
        if (contentData && formRef.current && installmentOptions.length > 0) {
            formRef.current.setFieldsValue({
                title: contentData.title,
                description: contentData.description,
                createdAt: contentData.createdAt,
                videoUrl: contentData.videoUrl,
                driveUrl: contentData.driveUrl,
                zoomUrl: contentData.zoomUrl,
                isPublished: contentData.isPublished ? 'true' : 'false',
                isFree: contentData.isFree ? true : false,
                installment: contentData.installment !== null && contentData.installment !== undefined ? contentData.installment : 0
            });
        }
    }, [contentData, formRef, isOpen, installmentOptions]);


    const onFinish = async (values) => {
        Object.keys(values).forEach(key => (values[key] === undefined || values[key] === '') && delete values[key]);

        values.id = contentData.id;

        if (values.isPublished === 'true') {
            values.isPublished = true;
        } else {
            values.isPublished = false;
        }

        if (values.isFree === true) {
            values.isFree = true;
        } else {
            values.isFree = false;
        }

        values.driveUrl = normalizeDriveUrlForSave(values.driveUrl);

        setIsLoading(true);

        try {
            const res = await editCourseContent(values);
            if (res && res.status === 200) {
                openNotification('success',
                    'Course Content Updated',
                    'New course content updated successfully'
                );
                setOpen(false);
                await hadleGetCourceContents();
            }
        } catch (error) {
            console.error('AddCource', error);
        }

        setIsLoading(false);
    }

    const openNotification = (type, message, description) => {
        api[type]({
            message: message,
            description: description,
        });
    }

    return (
        <div>
            {contextHolder}
            <Drawer
                title="Edit Course Content"
                width={720}
                onClose={() => setOpen(false)}
                open={isOpen}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                <Spin
                    spinning={isLoading}
                    tip="Please wait..."
                >
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        ref={formRef}
                    >
                        <Form.Item
                            label="Title"
                            name="title" 
                            rules={[{ required: true, message: 'Please input the title!' }]}
                        >
                            <Input
                                showCount
                                maxLength={200}
                                placeholder="Title" />
                        </Form.Item>
                        <Form.Item
                            label="Description"
                            name="description">
                            <ReactQuill
                                modules={modules}
                                theme="snow"
                                placeholder="Description"
                                style={{ height: '200px', marginBottom: '50px' }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Video ID"
                            name="videoUrl">
                            <Input
                                showCount
                                maxLength={500}
                                placeholder="Video ID" />
                        </Form.Item>
                        <Form.Item
                            label="Zoom Recording URL"
                            name="zoomUrl">
                            <Input
                                showCount
                                maxLength={500}
                                placeholder="Zoom Recording URL" />
                        </Form.Item>
                        <Form.Item
                            label="Drive URL"
                            name="driveUrl"
                            extra="Paste Google Drive folder or file link. Ensure sharing is set to 'Anyone with the link can view'."
                        >
                            <Input
                                showCount
                                maxLength={500}
                                placeholder="e.g. https://drive.google.com/drive/folders/..." />
                        </Form.Item>
                        <Form.Item
                            label="Is Free"
                            name="isFree"
                            rules={[{ required: true, message: 'Please check isFree!' }]}
                        >
                            <Radio.Group>
                                <Radio value={true}>Free</Radio>
                                <Radio value={false}>Paid</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            label="Is Published"
                            name="isPublished"
                            rules={[{ required: true, message: 'Please check isPublished!' }]}
                        >
                            <Select
                                placeholder="Please select course content published status"
                            >
                                <Option value="true">Published</Option>
                                <Option value="false">Not Published</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Installments"
                            name="installment"
                            rules={[{ required: true, message: 'Please select installment!' }]}
                        >
                            <Select
                                placeholder="Please select installment level"
                                options={installmentOptions}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col>
                                <Button
                                    style={{ width: '100%' }}
                                    type="primary" htmlType="submit"
                                >
                                    Update
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Drawer>
        </div>
    )
}

export default EditContent